import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const CostCalculator = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    serviceType: '',
    propertyType: '',
    squareFootage: '',
    rooms: '',
    frequency: '',
    addons: []
  });
  const [calculatedCost, setCalculatedCost] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const serviceTypeOptions = [
    { value: 'house-cleaning', label: 'House Cleaning' },
    { value: 'office-cleaning', label: 'Office Cleaning' },
    { value: 'carpet-cleaning', label: 'Carpet Cleaning' },
    { value: 'deep-cleaning', label: 'Deep Cleaning' },
    { value: 'post-construction', label: 'Post-Construction Cleaning' },
    { value: 'disinfection', label: 'Disinfection Service' }
  ];

  const propertyTypeOptions = [
    { value: 'apartment', label: 'Apartment' },
    { value: 'house', label: 'House' },
    { value: 'office', label: 'Office' },
    { value: 'commercial', label: 'Commercial Space' }
  ];

  const frequencyOptions = [
    { value: 'one-time', label: 'One-time Service' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'bi-weekly', label: 'Bi-weekly' },
    { value: 'monthly', label: 'Monthly' }
  ];

  const addonOptions = [
    { value: 'inside-oven', label: 'Inside Oven Cleaning (+$25)' },
    { value: 'inside-fridge', label: 'Inside Refrigerator (+$20)' },
    { value: 'windows', label: 'Window Cleaning (+$30)' },
    { value: 'garage', label: 'Garage Cleaning (+$40)' },
    { value: 'basement', label: 'Basement Cleaning (+$35)' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddonChange = (addonValue, checked) => {
    setFormData(prev => ({
      ...prev,
      addons: checked 
        ? [...prev?.addons, addonValue]
        : prev?.addons?.filter(addon => addon !== addonValue)
    }));
  };

  const calculateCost = async () => {
    if (!formData?.serviceType || !formData?.propertyType || !formData?.squareFootage) {
      return;
    }

    setIsCalculating(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Base pricing logic
      let baseCost = 0;
      const sqft = parseInt(formData?.squareFootage);

      switch (formData?.serviceType) {
        case 'house-cleaning':
          baseCost = sqft * 0.15;
          break;
        case 'office-cleaning':
          baseCost = sqft * 0.12;
          break;
        case 'carpet-cleaning':
          baseCost = sqft * 0.25;
          break;
        case 'deep-cleaning':
          baseCost = sqft * 0.20;
          break;
        case 'post-construction':
          baseCost = sqft * 0.30;
          break;
        case 'disinfection':
          baseCost = sqft * 0.18;
          break;
        default:
          baseCost = sqft * 0.15;
      }

      // Frequency discount
      let frequencyMultiplier = 1;
      switch (formData?.frequency) {
        case 'weekly':
          frequencyMultiplier = 0.85;
          break;
        case 'bi-weekly':
          frequencyMultiplier = 0.90;
          break;
        case 'monthly':
          frequencyMultiplier = 0.95;
          break;
      }

      // Add-ons cost
      const addonCosts = {
        'inside-oven': 25,
        'inside-fridge': 20,
        'windows': 30,
        'garage': 40,
        'basement': 35
      };

      const totalAddonCost = formData?.addons?.reduce((total, addon) => {
        return total + (addonCosts?.[addon] || 0);
      }, 0);

      const finalCost = (baseCost * frequencyMultiplier) + totalAddonCost;

      setCalculatedCost({
        baseCost: baseCost,
        frequencyDiscount: baseCost - (baseCost * frequencyMultiplier),
        addonCost: totalAddonCost,
        totalCost: finalCost,
        savings: baseCost - (baseCost * frequencyMultiplier)
      });

    } catch (error) {
      console.error('Calculation error:', error);
    } finally {
      setIsCalculating(false);
    }
  };

  const resetCalculator = () => {
    setFormData({
      serviceType: '',
      propertyType: '',
      squareFootage: '',
      rooms: '',
      frequency: '',
      addons: []
    });
    setCalculatedCost(null);
  };

  const handleBookService = () => {
    console.log('Booking service with calculated cost:', calculatedCost);
    // Navigate to booking page with pre-filled data
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-elevation-5 w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Calculator" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-text-primary">
                Cleaning Cost Calculator
              </h2>
              <p className="text-sm text-text-secondary">
                Get an instant estimate for your cleaning needs
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close calculator"
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-96 overflow-y-auto">
          {!calculatedCost ? (
            /* Calculator Form */
            (<div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Service Type"
                  options={serviceTypeOptions}
                  value={formData?.serviceType}
                  onChange={(value) => handleInputChange('serviceType', value)}
                  placeholder="Select service type"
                  required
                />
                
                <Select
                  label="Property Type"
                  options={propertyTypeOptions}
                  value={formData?.propertyType}
                  onChange={(value) => handleInputChange('propertyType', value)}
                  placeholder="Select property type"
                  required
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Square Footage"
                  type="number"
                  placeholder="Enter square footage"
                  value={formData?.squareFootage}
                  onChange={(e) => handleInputChange('squareFootage', e?.target?.value)}
                  required
                />
                
                <Input
                  label="Number of Rooms"
                  type="number"
                  placeholder="Enter number of rooms"
                  value={formData?.rooms}
                  onChange={(e) => handleInputChange('rooms', e?.target?.value)}
                />
              </div>
              <Select
                label="Service Frequency"
                options={frequencyOptions}
                value={formData?.frequency}
                onChange={(value) => handleInputChange('frequency', value)}
                placeholder="Select frequency"
                description="Regular services receive discounts"
              />
              {/* Add-ons */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-3">
                  Additional Services (Optional)
                </label>
                <div className="space-y-2">
                  {addonOptions?.map((addon) => (
                    <label key={addon?.value} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-muted cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData?.addons?.includes(addon?.value)}
                        onChange={(e) => handleAddonChange(addon?.value, e?.target?.checked)}
                        className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                      />
                      <span className="text-sm text-text-primary">{addon?.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex space-x-3">
                <Button
                  variant="default"
                  onClick={calculateCost}
                  loading={isCalculating}
                  iconName="Calculator"
                  iconPosition="left"
                  className="flex-1"
                  disabled={!formData?.serviceType || !formData?.propertyType || !formData?.squareFootage}
                >
                  Calculate Cost
                </Button>
                <Button
                  variant="outline"
                  onClick={resetCalculator}
                  iconName="RotateCcw"
                  iconPosition="left"
                >
                  Reset
                </Button>
              </div>
            </div>)
          ) : (
            /* Results Display */
            (<div className="space-y-6">
              <div className="text-center">
                <h3 className="text-2xl font-bold text-text-primary mb-2">
                  Your Estimate
                </h3>
                <p className="text-text-secondary">
                  Based on your selections
                </p>
              </div>
              {/* Cost Breakdown */}
              <div className="bg-surface rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-text-secondary">Base Service Cost</span>
                  <span className="font-mono">${calculatedCost?.baseCost?.toFixed(2)}</span>
                </div>
                
                {calculatedCost?.savings > 0 && (
                  <div className="flex justify-between items-center text-success">
                    <span>Frequency Discount</span>
                    <span className="font-mono">-${calculatedCost?.savings?.toFixed(2)}</span>
                  </div>
                )}
                
                {calculatedCost?.addonCost > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-text-secondary">Additional Services</span>
                    <span className="font-mono">+${calculatedCost?.addonCost?.toFixed(2)}</span>
                  </div>
                )}
                
                <div className="border-t border-border pt-3">
                  <div className="flex justify-between items-center text-lg font-semibold">
                    <span className="text-text-primary">Total Estimate</span>
                    <span className="text-primary font-mono">${calculatedCost?.totalCost?.toFixed(2)}</span>
                  </div>
                </div>
              </div>
              {/* Service Details */}
              <div className="bg-primary/5 rounded-lg p-4">
                <h4 className="font-medium text-text-primary mb-2">Service Details</h4>
                <div className="space-y-1 text-sm text-text-secondary">
                  <p>Service: {serviceTypeOptions?.find(s => s?.value === formData?.serviceType)?.label}</p>
                  <p>Property: {propertyTypeOptions?.find(p => p?.value === formData?.propertyType)?.label}</p>
                  <p>Size: {formData?.squareFootage} sq ft</p>
                  {formData?.frequency && (
                    <p>Frequency: {frequencyOptions?.find(f => f?.value === formData?.frequency)?.label}</p>
                  )}
                </div>
              </div>
              {/* Action Buttons */}
              <div className="flex space-x-3">
                <Button
                  variant="default"
                  onClick={handleBookService}
                  iconName="Calendar"
                  iconPosition="left"
                  className="flex-1"
                >
                  Book This Service
                </Button>
                <Button
                  variant="outline"
                  onClick={resetCalculator}
                  iconName="ArrowLeft"
                  iconPosition="left"
                >
                  New Quote
                </Button>
              </div>
              {/* Disclaimer */}
              <div className="text-xs text-text-secondary text-center bg-muted rounded-lg p-3">
                <Icon name="Info" size={14} className="inline mr-1" />
                This is an estimate. Final pricing may vary based on specific requirements and site inspection.
              </div>
            </div>)
          )}
        </div>
      </div>
    </div>
  );
};

export default CostCalculator;